import React, {useEffect, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {Dropdown} from 'primereact/dropdown';
import axios from 'axios';

const MIN_SIZE = 0.01;

const getStatusColor = (status) => {
  switch (status) {
    case 'GOOD': return '#2e7d32';
    case 'MEDIUM': return '#f9a825';
    case 'BAD': return '#c62828';
    default: return '#d84315';
  }
};

const getTranslatedStatusLabel = (status, t) => {
  switch (status) {
    case 'GOOD': return t('good');
    case 'MEDIUM': return t('notBad');
    case 'BAD': return t('bad');
    default: return '-';
  }
};

const getStatusBackgroundColor = (status, isPreview) => {
  if (isPreview) return 'rgba(25,118,210,0.10)';
  switch (status) {
    case 'GOOD': return 'rgba(46,125,50,0.14)';
    case 'MEDIUM': return 'rgba(249,168,37,0.16)';
    case 'BAD': return 'rgba(198,40,40,0.14)';
    default: return 'rgba(216,67,21,0.12)';
  }
};

const FloorplanComponent = (props) => {
  const {t} = props;
  const statusOptions = [
    {label: t('good'), value: 'GOOD'},
    {label: t('notBad'), value: 'MEDIUM'},
    {label: t('bad'), value: 'BAD'}
  ];
  const {floorPlanId, onSave} = props;
  const [machines, setMachines] = useState([]);
  const [floorPlan, setFloorPlan] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null); 
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [rectangles, setRectangles] = useState([]);
  const [originalRectangles, setOriginalRectangles] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingRect, setDrawingRect] = useState(null);

  const [renameDialogVisible, setRenameDialogVisible] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [statusValue, setStatusValue] = useState('GOOD');
  const [editingRectangleId, setEditingRectangleId] = useState(null);

  const [draggingRectangleId, setDraggingRectangleId] = useState(null);
  const [dragStartPointer, setDragStartPointer] = useState(null);
  const [dragStartGeometry, setDragStartGeometry] = useState(null);

  const [resizingRectangleId, setResizingRectangleId] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStartPointer, setResizeStartPointer] = useState(null);
  const [resizeStartGeometry, setResizeStartGeometry] = useState(null);

  const [zoom, setZoom] = useState(1);

  const stageRef = useRef(null);
  const suppressClickRef = useRef(false);
  const fileInputRef = useRef(null);

  const commonHeaders = {
    'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
    'App-Network': localStorage.getItem("networkId")
  };

  const fetchImageBlob = async (fileId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/file/${fileId}/download`, {
        headers: commonHeaders,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setImageBlobUrl(url);
    } catch (err) {
      console.error("Kép letöltési hiba", err);
    }
  };

  const loadFloorPlanData = async (id) => {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/floor-plan/${id}`, {
      headers: commonHeaders
    });
    const data = response?.data?.data;
    if (data) {
      setFloorPlan(data);
      setUploadedFileName(data.name);
      if (data.file?.id) {
        fetchImageBlob(data.file.id);
      }
      const existingRects = (data.floor_plan_machines || []).map(m => ({
        id: m.id,
        machine: m.machine,
        status: m.status || 'GOOD',
        dimension_x: m.dimension_x,
        dimension_y: m.dimension_y,
        width: m.width,
        height: m.height
      }));
      setRectangles(existingRects);
      setOriginalRectangles(existingRects);
    }
  };

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_HOST + '/api/v2/machine?item_per_page=1000', {
      headers: commonHeaders
    })
      .then(response => setMachines(response?.data?.data?.items || []))
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    if (floorPlanId) {
      loadFloorPlanData(floorPlanId)
        .catch(error => console.error("Betöltési hiba", error));
    } else {
      setFloorPlan(null);
      setRectangles([]);
      setOriginalRectangles([]);
      setImageBlobUrl('');
      setUploadedFile(null);
      setUploadedFileName('');
    }
  }, [floorPlanId]);

  const normalizeNumber = (value) => Number(value).toFixed(6);

  const buildRectPayload = (rect, floorPlanId) => ({
    floor_plan: floorPlanId,
    machine: rect.machine?.id || rect.machine,
    dimension_x: Number(rect.dimension_x),
    dimension_y: Number(rect.dimension_y),
    width: Number(rect.width),
    height: Number(rect.height),
    status: rect.status || 'GOOD'
  });

  const rectSignature = (rect) => {
    const machineId = rect.machine?.id || rect.machine || null;
    return JSON.stringify({
      machine: machineId,
      status: rect.status || 'GOOD',
      dimension_x: Number(rect.dimension_x).toFixed(6),
      dimension_y: Number(rect.dimension_y).toFixed(6),
      width: Number(rect.width).toFixed(6),
      height: Number(rect.height).toFixed(6)
    });
  };

  const dedupeRectanglesForSave = (rectangles, floorPlanId) => {
    const uniqueMap = new Map();
    rectangles
      .filter(rect => rect.machine !== null && rect.machine !== undefined)
      .forEach(rect => {
        const payload = buildRectPayload(rect, floorPlanId);
        const isExisting = rect.id && !String(rect.id).startsWith('rect-');
        const uniqueKey = isExisting
          ? `existing:${rect.id}:${payload.machine}:${normalizeNumber(payload.dimension_x)}:${normalizeNumber(payload.dimension_y)}:${normalizeNumber(payload.width)}:${normalizeNumber(payload.height)}:${payload.status}`
          : `new:${payload.machine}:${normalizeNumber(payload.dimension_x)}:${normalizeNumber(payload.dimension_y)}:${normalizeNumber(payload.width)}:${normalizeNumber(payload.height)}:${payload.status}`;
        if (!uniqueMap.has(uniqueKey)) {
          uniqueMap.set(uniqueKey, {
            rect,
            payload,
            isExisting
          });
        }
      });
    return Array.from(uniqueMap.values());
  };

  const handleFinalSave = async () => {
    let currentFileId = floorPlan?.file?.id || null;
    let currentFloorPlanId =
      (floorPlan?.id && !floorPlan.id.toString().includes('uploaded'))
        ? floorPlan.id
        : null;

    try {
      if (uploadedFile) {
        const formData = new FormData();
        formData.append('file', uploadedFile);
        const fileRes = await axios.post(
          process.env.REACT_APP_API_HOST + '/api/v2/file/upload',
          formData,
          {
            headers: {
              ...commonHeaders,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        currentFileId = fileRes.data.data.id;
      }

      if (!currentFloorPlanId || uploadedFile) {
        const fpPayload = {
          name: uploadedFileName || 'Új alaprajz',
          description: 'Üzemi alaprajz',
          file: currentFileId
        };
        const fpRes = await axios.post(
          process.env.REACT_APP_API_HOST + '/api/v2/floor-plan',
          fpPayload,
          { headers: commonHeaders }
        );
        currentFloorPlanId = fpRes.data.data.id;
      }
      const uniqueRectsToSave = dedupeRectanglesForSave(rectangles, currentFloorPlanId);
      const originalMap = new Map(
        originalRectangles.map(rect => [String(rect.id), rectSignature(rect)])
      );
      for (const item of uniqueRectsToSave) {
        if (item.isExisting) {
          const originalSignature = originalMap.get(String(item.rect.id));
          const currentSignature = rectSignature(item.rect);

          if (originalSignature !== currentSignature) {
            await axios.patch(
              process.env.REACT_APP_API_HOST + '/api/v2/floor-plan-machine/' + item.rect.id,
              item.payload,
              { headers: commonHeaders }
            );
          }
        } else {
          await axios.post(
            process.env.REACT_APP_API_HOST + '/api/v2/floor-plan-machine',
            item.payload,
            { headers: commonHeaders }
          );
        }
      }
      await loadFloorPlanData(currentFloorPlanId);
      setUploadedFile(null);
      window.App.toastShow('success', 'Sikeres mentés!', 'Minden adat rögzítve lett.');
      if (onSave) onSave();
    } catch (error) {
      console.error(error);
      window.App.toastShow('error', 'Hiba', 'Sikertelen mentés.');
    }
  };

  useEffect(() => {
    return () => { if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl); };
  }, [imageBlobUrl]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Delete' || renameDialogVisible || !selectedItem?.id) return;
      setRectangles(prev => prev.filter(item => item.id !== selectedItem.id));
      setSelectedItem(null); setDialogVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedItem, renameDialogVisible]);

  const handleFloorPlanUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    const localUrl = URL.createObjectURL(file);
    setUploadedFile(file);
    setImageBlobUrl(localUrl);
    setFloorPlan({ id: 'uploaded-' + Date.now(), name: file.name, isUploaded: true });
    setUploadedFileName(file.name); setRectangles([]); setOriginalRectangles([]); setEditMode(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFloorPlan = () => {
    if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    setFloorPlan(null); setUploadedFile(null); setImageBlobUrl(''); setUploadedFileName(''); setRectangles([]); setOriginalRectangles([]); setEditMode(false);
  };

  const zoomIn = () => setZoom(prev => Math.min(3, Number((prev + 0.1).toFixed(2))));
  const zoomOut = () => setZoom(prev => Math.max(0.4, Number((prev - 0.1).toFixed(2))));
  const resetZoom = () => setZoom(1);

  const getRelativePosition = (event) => {
    if (!stageRef.current) return null;
    const rect = stageRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
    };
  };

  const clampGeometry = (geometry) => {
    let {dimension_x, dimension_y, width, height} = geometry;
    width = Math.max(MIN_SIZE, Math.min(1, width));
    height = Math.max(MIN_SIZE, Math.min(1, height));
    dimension_x = Math.max(0, Math.min(1 - width, dimension_x));
    dimension_y = Math.max(0, Math.min(1 - height, dimension_y));
    return {dimension_x, dimension_y, width, height};
  };

  const handlePointerDown = (event) => {
    if (!floorPlan || !editMode || draggingRectangleId || resizingRectangleId || event.button !== 0) return;
    if (event.target?.dataset?.role === 'rectangle-control') return;
    event.preventDefault(); event.stopPropagation();
    const pos = getRelativePosition(event); if (!pos) return;
    setDialogVisible(false); setSelectedItem(null); setIsDrawing(true);
    setDrawingRect({ startX: pos.x, startY: pos.y, dimension_x: pos.x, dimension_y: pos.y, width: 0, height: 0 });
  };

  const applyResize = (startGeometry, handle, deltaX, deltaY) => {
    let {dimension_x, dimension_y, width, height} = startGeometry;
    const right = dimension_x + width; const bottom = dimension_y + height;
    let nL = dimension_x, nT = dimension_y, nR = right, nB = bottom;
    if (handle.includes('e')) nR = Math.max(dimension_x + MIN_SIZE, right + deltaX);
    if (handle.includes('s')) nB = Math.max(dimension_y + MIN_SIZE, bottom + deltaY);
    if (handle.includes('w')) nL = Math.min(right - MIN_SIZE, dimension_x + deltaX);
    if (handle.includes('n')) nT = Math.min(bottom - MIN_SIZE, dimension_y + deltaY);
    return clampGeometry({ dimension_x: nL, dimension_y: nT, width: nR - nL, height: nB - nT });
  };

  const handlePointerMove = (event) => {
    const pos = getRelativePosition(event); if (!pos) return;
    if (draggingRectangleId && dragStartPointer && dragStartGeometry) {
      event.preventDefault(); suppressClickRef.current = true;
      const dX = pos.x - dragStartPointer.x, dY = pos.y - dragStartPointer.y;
      setRectangles(prev => prev.map(item => item.id !== draggingRectangleId ? item : {
        ...item, dimension_x: Math.max(0, Math.min(1 - item.width, dragStartGeometry.dimension_x + dX)),
        dimension_y: Math.max(0, Math.min(1 - item.height, dragStartGeometry.dimension_y + dY))
      }));
      return;
    }
    if (resizingRectangleId && resizeHandle && resizeStartPointer && resizeStartGeometry) {
      event.preventDefault(); suppressClickRef.current = true;
      const nextGeo = applyResize(resizeStartGeometry, resizeHandle, pos.x - resizeStartPointer.x, pos.y - resizeStartPointer.y);
      setRectangles(prev => prev.map(item => item.id !== resizingRectangleId ? item : {...item, ...nextGeo}));
      return;
    }
    if (!floorPlan || !editMode || !isDrawing || !drawingRect) return;
    setDrawingRect(prev => ({ ...prev, dimension_x: Math.min(prev.startX, pos.x), dimension_y: Math.min(prev.startY, pos.y), width: Math.abs(pos.x - prev.startX), height: Math.abs(pos.y - prev.startY) }));
  };

  const handlePointerUp = () => {
    if (draggingRectangleId || resizingRectangleId) {
      setDraggingRectangleId(null); setResizingRectangleId(null); return;
    }
    if (isDrawing && drawingRect && drawingRect.width > 0.005) {
      const created = { id: 'rect-' + Date.now(), machine: null, status: 'GOOD', ...drawingRect };
      setRectangles(prev => [...prev, created]); setSelectedItem(created);
    }
    setIsDrawing(false); setDrawingRect(null);
  };

  const startDraggingRectangle = (event, item) => {
    if (!editMode) return;
    event.preventDefault(); event.stopPropagation();
    const pos = getRelativePosition(event); if (!pos) return;
    suppressClickRef.current = true; setDraggingRectangleId(item.id); setDragStartPointer(pos);
    setDragStartGeometry({...item}); setIsDrawing(false); setDialogVisible(false);
  };

  const startResizeRectangle = (event, item, handle) => {
    if (!editMode) return;
    event.preventDefault(); event.stopPropagation();
    const pos = getRelativePosition(event); if (!pos) return;
    suppressClickRef.current = true; setResizingRectangleId(item.id); setResizeHandle(handle); setResizeStartPointer(pos);
    setResizeStartGeometry({...item}); setIsDrawing(false); setDialogVisible(false);
  };

  const openEditDialog = (item) => {
    setEditingRectangleId(item.id); setSelectedMachine(item.machine?.id || item.machine || null);
    setStatusValue(item.status || 'GOOD'); setRenameDialogVisible(true);
  };

  const openRenameDialog = (event, item) => {
    event.preventDefault(); event.stopPropagation(); openEditDialog(item);
  };

  const saveRename = () => {
    const machineObj = machines.find(m => m.id === selectedMachine);
    const duplicateMachine = rectangles.find(item => {
      const itemMachineId = item.machine?.id || item.machine;
      return item.id !== editingRectangleId && itemMachineId === selectedMachine;
    });
    if (duplicateMachine) {
      window.App.toastShow('error', 'Hiba', 'Ez a gép már hozzá van rendelve.');
      return;
    }
    setRectangles(prev => prev.map(item => item.id !== editingRectangleId ? item : {
      ...item,
      machine: machineObj || selectedMachine,
      status: statusValue || 'GOOD'
    }));
    if (selectedItem?.id === editingRectangleId) {
      setSelectedItem(prev => ({
        ...prev,
        machine: machineObj || selectedMachine,
        status: statusValue || 'GOOD'
      }));
    }
    setRenameDialogVisible(false);
  };

  const openEditFromList = (item) => openEditDialog(item);

  const deleteRectangle = (id) => {
    setRectangles(prev => prev.filter(item => item.id !== id));
    if (selectedItem?.id === id) { setSelectedItem(null); setDialogVisible(false); }
  };

  const allRectangles = drawingRect ? [...rectangles, { id: 'preview', status: 'GOOD', isPreview: true, ...drawingRect }] : rectangles;

  const resizeHandles = [
    {key: 'nw', left: '-5px', top: '-5px', cursor: 'nwse-resize'},
    {key: 'n', left: '50%', top: '-5px', transform: 'translateX(-50%)', cursor: 'ns-resize'},
    {key: 'ne', right: '-5px', top: '-5px', cursor: 'nesw-resize'},
    {key: 'e', right: '-5px', top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize'},
    {key: 'se', right: '-5px', bottom: '-5px', cursor: 'nwse-resize'},
    {key: 's', left: '50%', bottom: '-5px', transform: 'translateX(-50%)', cursor: 'ns-resize'},
    {key: 'sw', left: '-5px', bottom: '-5px', cursor: 'nesw-resize'},
    {key: 'w', left: '-5px', top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize'}
  ];

  return (
    <div style={{position: 'relative'}}>
      <div className="p-grid p-ai-center p-mb-3">
        <div className="p-col-12 p-lg-4">
          <h3 className="p-m-0 text-primary">{floorPlan?.name || t('floorPlanManagement')}</h3>
        </div>
        <div className="p-col-12 p-lg-8 p-text-right">
          <div style={{display: 'inline-flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
            {localStorage.getItem('roles').toString().includes('role_floor_plan_write') && (
              <label className="p-button p-button-success" style={{margin: 0, cursor: 'pointer'}}>
                <i className="pi pi-upload p-mr-2"></i> {t('upload')}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFloorPlanUpload} style={{display: 'none'}} />
              </label>
            )}
            {floorPlan && (
              <>
                <Button type="button" icon="pi pi-search-minus" className="p-button-secondary" onClick={zoomOut} />
                <Button type="button" label={`${Math.round(zoom * 100)}%`} className="p-button-secondary" onClick={resetZoom} />
                <Button type="button" icon="pi pi-search-plus" className="p-button-secondary" onClick={zoomIn} />
                <Button type="button" label={editMode ? t('finishEditing') : t('editAction')} className={editMode ? 'p-button-warning' : 'p-button-primary'} onClick={() => setEditMode(!editMode)} />
                <Button label={t('save')} icon="pi pi-save" className="p-button-success" onClick={handleFinalSave} />
                <Button type="button" label={t('deleteImage')} className="p-button-secondary" onClick={clearFloorPlan} />
              </>
            )}
          </div>
        </div>
      </div>

      {floorPlan && editMode && (
        <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '4px', fontSize: '14px' }}>
          {t('floorPlanEditModeHint')}
        </div>
      )}

      {!floorPlan && (
        <div style={{border: '1px dashed #cfd8dc', background: '#fafafa', borderRadius: '6px', padding: '40px 20px', textAlign: 'center', color: '#666'}}>
          {t('floorPlanUploadHint')}
        </div>
      )}

      {floorPlan && (
        <div style={{width: '100%', height: '70vh', border: '1px solid #dcdfe6', background: '#f8f9fa', overflow: 'auto', position: 'relative'}}>
          <div style={{width: `${zoom * 100}%`, transformOrigin: 'top left', position: 'relative'}}>
            <div ref={stageRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} style={{position: 'relative', width: '100%', cursor: editMode ? 'crosshair' : 'default', touchAction: 'none'}}>
              <img src={imageBlobUrl || ''} alt="Plan" style={{display: 'block', width: '100%', height: 'auto', pointerEvents: 'none'}} />
              <div style={{position: 'absolute', inset: 0, zIndex: 2}}>
                {allRectangles.map(item => (
                  <div key={item.id} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (suppressClickRef.current) { suppressClickRef.current = false; return; } if (!item.isPreview) { setSelectedItem(item); setDialogVisible(true); } }}
                    style={{ position: 'absolute', left: (item.dimension_x * 100) + '%', top: (item.dimension_y * 100) + '%', width: (item.width * 100) + '%', height: (item.height * 100) + '%', border: item.isPreview ? '2px dashed #1976d2' : `3px solid ${getStatusColor(item.status)}`, backgroundColor: getStatusBackgroundColor(item.status, item.isPreview), boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'visible' }}>
                    {!item.isPreview && editMode && (
                      <div style={{position: 'absolute', top: '-14px', right: '-1px', display: 'flex', gap: '4px', zIndex: 6}}>
                        <button type="button" onClick={(e) => openRenameDialog(e, item)} style={{width: '24px', height: '24px', border: '1px solid #d0d0d0', background: '#fff', borderRadius: '4px', cursor: 'pointer'}}><i className="pi pi-pencil" style={{fontSize: '12px'}}></i></button>
                        <button type="button" onPointerDown={(e) => startDraggingRectangle(e, item)} style={{width: '24px', height: '24px', border: '1px solid #d0d0d0', background: '#fff', borderRadius: '4px', cursor: 'move'}}><i className="pi pi-arrows-alt" style={{fontSize: '12px'}}></i></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); deleteRectangle(item.id); }} style={{width: '24px', height: '24px', border: '1px solid #d0d0d0', background: '#fff', borderRadius: '4px', cursor: 'pointer'}}><i className="pi pi-trash" style={{fontSize: '12px'}}></i></button>
                      </div>
                    )}
                    {!item.isPreview && editMode && resizeHandles.map(h => (<div key={h.key} onPointerDown={(e) => startResizeRectangle(e, item, h.key)} style={{position: 'absolute', width: '10px', height: '10px', background: '#fff', border: `1px solid ${getStatusColor(item.status)}`, borderRadius: '2px', zIndex: 7, cursor: h.cursor, ...h}} />))}
                    {!item.isPreview && <div style={{fontSize: '12px', fontWeight: 700, color: '#111', background: 'rgba(255,255,255,0.88)', padding: '2px 6px', marginBottom: '4px', borderRadius: '4px', maxWidth: 'calc(100% - 8px)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none'}}>{item?.machine?.name || item?.label || '-'}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-mt-3">
        <h4>{t('machinesOnFloorPlan')}</h4>
        {rectangles.map((item, index) => (
          <div key={item.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '4px', marginBottom: '8px'}}>
            <div><b>{item.machine?.name || item.label || `${index + 1}. ${t('rectangle')}`}</b><div style={{fontSize: '12px', color: '#666'}}>{t('status')}: {getTranslatedStatusLabel(item.status, t)} | x: {item.dimension_x.toFixed(4)} | y: {item.dimension_y.toFixed(4)}</div></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto'}}><Button type="button" icon="pi pi-pencil" className="p-button-warning p-button-sm" onClick={() => openEditFromList(item)} /><Button type="button" icon="pi pi-trash" className="p-button-danger p-button-sm" onClick={() => deleteRectangle(item.id)} /></div>
          </div>
        ))}
      </div>

      <Dialog header={selectedItem?.machine?.name || 'Gép adatai'} visible={dialogVisible} style={{width: '50vw'}} modal onHide={() => setDialogVisible(false)}>
        {selectedItem && (
          <div className="p-fluid">
            <div className="field p-mb-3"><label><b>{t('machine')}</b></label><div>{selectedItem.machine?.name || '-'}</div></div>
            <div className="field p-mb-3"><label><b>{t('status')}</b></label><div>{getTranslatedStatusLabel(selectedItem.status, t)}</div></div>
            <div className="field p-mb-3"><label><b>X</b></label><div>{selectedItem.dimension_x}</div></div>
            <div className="field p-mb-3"><label><b>Y</b></label><div>{selectedItem.dimension_y}</div></div>
          </div>
        )}
      </Dialog>

      <Dialog header={t('editMachine')} visible={renameDialogVisible} style={{width: '450px'}} modal onHide={() => setRenameDialogVisible(false)} footer={<div><Button type="button" label={t('cancel')} className="p-button-text" onClick={() => setRenameDialogVisible(false)} /><Button type="button" label={t('save')} onClick={saveRename} /></div>}>
        <div className="p-fluid">
          <div className="field p-mb-3"><label><b>{t('machine')}</b></label><Dropdown value={selectedMachine} options={machines} optionLabel="name" optionValue="id" onChange={(e) => setSelectedMachine(e.value)} placeholder={t('selectMachine')} filter style={{width: '100%'}} /></div>
          <div className="field"><label><b>{t('condition')}</b></label><Dropdown value={statusValue} options={statusOptions} optionLabel="label" optionValue="value" onChange={(e) => setStatusValue(e.value)} style={{width: '100%'}} /></div>
        </div>
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(FloorplanComponent);
